import { useCallback } from 'react';

import { type usePublisherCategorySelector } from '../../publisher-category-selector/hooks/usePublisherCategorySelector';
import { type useSchoolCategorySelector } from '../../school-category-selector/hooks/useSchoolCategorySelector';
import { type useSubjectCategorySelector } from '../../subject-category-selector/hooks/useSubjectCategorySelector';

/**
 * Category cascading reset hook
 *
 * 상위 레벨 선택 시 하위 모든 레벨을 자동으로 리셋합니다.
 * useCallback을 사용하여 항상 최신 state를 참조합니다.
 *
 * 레벨 체계:
 * - Level 0: SCHOOL_TYPE
 * - Level 1: GRADE
 * - Level 2: SEMESTER
 * - Level 3: SUBJECT
 * - Level 4: SUB_SUBJECT
 * - Level 5: PUBLISHER
 *
 * @example
 * const resetHandlers = useCategoryReset({ schoolState, subjectState, publisherState });
 * resetHandlers.resetFromSchoolType(); // 모든 하위 레벨 리셋
 */
export const useCategoryReset = ({
  schoolState,
  subjectState,
  publisherState,
}: {
  schoolState: ReturnType<typeof useSchoolCategorySelector>;
  subjectState: ReturnType<typeof useSubjectCategorySelector>;
  publisherState: ReturnType<typeof usePublisherCategorySelector>;
}) => {
  // Individual reset functions
  const resetGrade = useCallback(() => {
    schoolState.clearError('gradeId');
    schoolState.gradeState.setSelectedGradeNode(undefined);
  }, [schoolState]);

  const resetSemester = useCallback(() => {
    schoolState.clearError('semesterId');
    schoolState.semesterState.setSelectedSemesterNode(undefined);
  }, [schoolState]);

  const resetSubject = useCallback(() => {
    subjectState.clearError();
    subjectState.handleSelectSubject(undefined);
  }, [subjectState]);

  const resetSubSubject = useCallback(() => {
    subjectState.handleSelectSubSubject(undefined);
  }, [subjectState]);

  const resetPublisher = useCallback(() => {
    publisherState.clearError('publisher');
    publisherState.clearError('customPublisher');
    publisherState.setPublisher(undefined);
    publisherState.setCustomPublisher('');
  }, [publisherState]);

  // Cascading reset handlers
  const resetFromSchoolType = useCallback(() => {
    // Reset all levels below SCHOOL_TYPE (Level 0)
    resetGrade();
    resetSemester();
    resetSubject();
    resetSubSubject();
    resetPublisher();
  }, [
    resetGrade,
    resetSemester,
    resetSubject,
    resetSubSubject,
    resetPublisher,
  ]);

  const resetFromGrade = useCallback(() => {
    // Reset all levels below GRADE (Level 1)
    resetSemester();
    resetSubject();
    resetSubSubject();
    resetPublisher();
  }, [resetSemester, resetSubject, resetSubSubject, resetPublisher]);

  const resetFromSemester = useCallback(() => {
    // Reset all levels below SEMESTER (Level 2)
    resetSubject();
    resetSubSubject();
    resetPublisher();
  }, [resetSubject, resetSubSubject, resetPublisher]);

  const resetFromSubject = useCallback(() => {
    // Reset all levels below SUBJECT (Level 3)
    resetSubSubject();
    resetPublisher();
  }, [resetSubSubject, resetPublisher]);

  const resetFromSubSubject = useCallback(() => {
    // Reset all levels below SUB_SUBJECT (Level 4)
    resetPublisher();
  }, [resetPublisher]);

  return {
    resetFromSchoolType,
    resetFromGrade,
    resetFromSemester,
    resetFromSubject,
    resetFromSubSubject,
  };
};
